import {
    PICKUP_WINDOW_IN_MS,
    canManageBooking,
    conflictingBooking,
    dateKey,
    formatBookingTime,
    hourOptions,
    initialMileage,
    isAuthBookingOwner,
    latestReturnMileage,
    timestampFor,
    validateMileage,
} from "./bookingUtils.js";

import { auth } from "../../db.js";

const BOOKINGS_LOCATION = "PoolCarBooking/Bookings";

const isAuthOwner = booking => isAuthBookingOwner(booking, auth.currentUser?.uid);
const canEditBooking = booking => canManageBooking(booking, auth.currentUser?.uid);

const bookingDateParts = timestamp => {
    const date = new Date(Number(timestamp));
    return { date: dateKey(date), hour: String(date.getHours()) };
};

const recordMileageTransaction = async ({ booking, db, mileage, stage, reason, source, store }) => {
    const result = await db.ref(`${BOOKINGS_LOCATION}/${booking.ID}/Mileage`).transaction(currentMileage => {
        if (!currentMileage) currentMileage = initialMileage();
        const recordedKey = stage === "pickup" ? "StartRecorded" : "EndRecorded";
        if (currentMileage[recordedKey] === true) return;

        if (stage === "return") {
            if (currentMileage.StartRecorded !== true) return;
            const startMileage = Number(currentMileage.Start?.Value);
            if (mileage < startMileage) return;
        }

        const timestamp = Date.now();
        const reading = {
            "Value": mileage,
            "Recorded": new Date(timestamp).toISOString(),
            "RecordedTimestamp": timestamp,
            "UserID": store.getters.userID,
            "AuthUID": auth.currentUser.uid,
            "Source": source,
        };
        if (reason) reading.Reason = reason;

        const updatedMileage = {
            ...currentMileage,
            [stage === "pickup" ? "Start" : "End"]: reading,
            [recordedKey]: true,
        };
        if (stage === "pickup" && reason) updatedMileage.ReviewRequired = true;
        if (stage === "return") {
            updatedMileage.Distance = mileage - Number(currentMileage.Start.Value);
            updatedMileage.Complete = true;
        }
        return updatedMileage;
    });

    if (!result.committed) {
        throw new Error(`This booking's ${stage} mileage could not be recorded. It may already have been submitted.`);
    }
};

export const recordPickupMileage = ({ form, booking, expectedMileage, db, store, source = "qr", onSubmitted }) => {
    const prefillMileage = expectedMileage != null && Number.isFinite(Number(expectedMileage)) ? String(expectedMileage) : "";
    form.setValues({ "Mileage": prefillMileage, "MismatchReason": "" });
    form.setConfig({
        "title": "Record Pool Car Pickup Mileage",
        "description": expectedMileage === null
            ? "Enter the odometer reading before driving the car."
            : `Enter the odometer reading before driving the car. The last recorded return was <strong>${expectedMileage}</strong>.`,
        "submitButtonLabel": "Record pickup",
        "type": "ignoreAutoAction",
        "location": `${BOOKINGS_LOCATION}/${booking.ID}`,
        "fields": [{
            "label": "Odometer mileage",
            "name": "Mileage",
            "type": "number",
        }, {
            "label": "Reason for lower mileage",
            "name": "MismatchReason",
            "type": "text",
            "required": false,
            show({ liveInputData }) {
                return expectedMileage !== null && Number(liveInputData.Mileage) < Number(expectedMileage);
            },
        }],
        preSubmitHook({ data }) {
            const mileage = validateMileage(data.Mileage);
            const mismatch = expectedMileage !== null && mileage < Number(expectedMileage);
            if (mismatch && !String(data.MismatchReason || "").trim()) {
                throw new Error("Enter a reason for the lower mileage reading.");
            }
            return { "Mileage": mileage };
        },
        async postSubmitHook({ data }) {
            await recordMileageTransaction({
                booking,
                db,
                mileage: data.Mileage,
                stage: "pickup",
                reason: String(data.MismatchReason || "").trim(),
                source,
                store,
            });
            await onSubmitted?.();
        },
        "successMessage": "Pickup mileage recorded",
    });
    form.toggle();
};

export const recordReturnMileage = ({ form, booking, db, store, source = "qr", onSubmitted }) => {
    const earlyReturn = Date.now() < Number(booking.End);
    form.setValues({ "Mileage": "", "ConfirmEarlyReturn": false });
    form.setConfig({
        "title": "Record Pool Car Return Mileage",
        "description": `Starting mileage: <strong>${booking.Mileage.Start.Value}</strong>${earlyReturn ? "<br>This will mark the car as returned before the scheduled booking end." : ""}`,
        "submitButtonLabel": "Record return",
        "type": "ignoreAutoAction",
        "location": `${BOOKINGS_LOCATION}/${booking.ID}`,
        "fields": [{
            "label": "Odometer mileage",
            "name": "Mileage",
            "type": "number",
        }, ...(earlyReturn ? [{
            "label": "Confirm early return",
            "name": "ConfirmEarlyReturn",
            "type": "toggle",
        }] : [])],
        preSubmitHook({ data }) {
            const mileage = validateMileage(data.Mileage);
            if (mileage < Number(booking.Mileage.Start.Value)) {
                throw new Error("Return mileage cannot be lower than pickup mileage.");
            }
            if (earlyReturn && data.ConfirmEarlyReturn !== true) {
                throw new Error("Confirm that the car is being returned early.");
            }
            return { "Mileage": mileage };
        },
        async postSubmitHook({ data }) {
            await recordMileageTransaction({ booking, db, mileage: data.Mileage, stage: "return", source, store });
            await onSubmitted?.();
        },
        "successMessage": "Return mileage recorded",
    });
    form.toggle();
};

export const recordBookingPickupMileage = async ({ form, pageItem: booking, db, store }) => {
    const bookings = await db.once(BOOKINGS_LOCATION) || {};
    recordPickupMileage({
        form,
        booking,
        db,
        store,
        source: "booking",
        expectedMileage: latestReturnMileage(bookings, booking.Start),
    });
};

export const recordBookingReturnMileage = ({ form, pageItem: booking, db, store }) => {
    recordReturnMileage({ form, booking, db, store, source: "booking" });
};

export const editBooking = ({ form, pageItem: booking, db }) => {
    if (!canEditBooking(booking)) return;

    const start = bookingDateParts(booking.Start);
    const end = bookingDateParts(booking.End);
    form.setValues({
        Date: start.date,
        EndDate: end.date,
        StartHour: start.hour,
        EndHour: end.hour,
        CommercialID: booking.CommercialID || "",
        Purpose: booking.Purpose || "",
    });
    form.setConfig({
        title: "Update Pool Car Booking",
        type: "ignoreAutoAction",
        submitButtonLabel: "Update booking",
        fields: [{
            label: "Start date", name: "Date", type: "date",
        }, {
            label: "End date", name: "EndDate", type: "date",
        }, {
            label: "Start", name: "StartHour", type: "dropdown",
            dropdownData: hourOptions(0, 24, true, false),
        }, {
            label: "End", name: "EndHour", type: "dropdown",
            dropdownData: hourOptions(0, 24, true, true),
        }, {
            label: "Commercial", name: "CommercialID", type: "dropdown",
            dataLocation: "Database/Commercials", labelKey: "Number", idKey: "ID",
        }, {
            label: "Purpose", name: "Purpose", type: "text", required: false,
            divClass: "md:col-span-3",
        }],
        async preSubmitHook({ data }) {
            if (!canEditBooking(booking)) {
                throw new Error("Only future bookings can be updated before pickup mileage is recorded.");
            }

            const updatedStart = timestampFor(data.Date, data.StartHour);
            const updatedEnd = timestampFor(data.EndDate, data.EndHour);
            if (!Number.isFinite(updatedStart) || !Number.isFinite(updatedEnd) || updatedEnd <= updatedStart) {
                throw new Error("The booking end date and time must be later than its start date and time.");
            }
            if (updatedStart <= Date.now()) throw new Error("The updated booking must start in the future.");

            const currentBookings = await db.once(BOOKINGS_LOCATION);
            const conflict = conflictingBooking(currentBookings, updatedStart, updatedEnd, booking.ID);
            if (conflict) {
                throw new Error(`The pool car is already booked by ${conflict.UserName || "another user"} from ${formatBookingTime(conflict)}.`);
            }

            const commercial = data.CommercialID ? await db.once(`Database/Commercials/${data.CommercialID}`) : null;
            if (data.CommercialID && !commercial) throw new Error("The selected commercial is not valid.");
            return {
                Start: updatedStart,
                End: updatedEnd,
                Commercial: commercial?.Number || null,
            };
        },
        async postSubmitHook({ data }) {
            await db.update(`${BOOKINGS_LOCATION}/${booking.ID}`, {
                Start: data.Start,
                End: data.End,
                CommercialID: data.CommercialID || null,
                Commercial: data.Commercial || null,
                Purpose: data.Purpose || null,
                Updated: new Date().toISOString(),
            });
            if (booking.CommercialID && booking.CommercialID !== data.CommercialID) {
                await db.delete(`Database/Commercials/${booking.CommercialID}/PoolCarBooking/${booking.ID}`);
            }
            if (data.CommercialID) {
                await db.set(`Database/Commercials/${data.CommercialID}/PoolCarBooking/${booking.ID}`, true);
            }
        },
        successMessage: "Pool car booking updated",
    });
    form.getDropdownData();
    form.toggle();
};

export const updateBookingAction = {
    label: "Update Booking",
    action: editBooking,
    show({ pageItem }) {
        return [canEditBooking(pageItem), "Only the assigned user or booker can update before the booking starts and pickup is recorded."];
    },
};

export const pickupMileageAction = {
    label: "Add Pickup Mileage",
    action: recordBookingPickupMileage,
    show({ pageItem }) {
        const available = isAuthOwner(pageItem)
            && pageItem?.Mileage?.StartRecorded !== true
            && Date.now() >= Number(pageItem?.Start) - PICKUP_WINDOW_IN_MS;
        return [available, "Pickup mileage opens 12 hours before your booking starts."];
    },
};

export const returnMileageAction = {
    label: "Add Drop Off Mileage",
    action: recordBookingReturnMileage,
    show({ pageItem }) {
        const available = isAuthOwner(pageItem)
            && pageItem?.Mileage?.StartRecorded === true
            && pageItem?.Mileage?.EndRecorded !== true;
        return [available, "Pickup mileage must be recorded before drop off mileage."];
    },
};

export const correctMileage = ({ form, rowItem: booking, db, store }) => {
    const hasPickup = booking?.Mileage?.StartRecorded === true;
    const hasReturn = booking?.Mileage?.EndRecorded === true;
    form.setValues({
        "PickupMileage": hasPickup ? booking.Mileage.Start.Value : "",
        "ReturnMileage": hasReturn ? booking.Mileage.End.Value : "",
        "Reason": "",
    });
    form.setConfig({
        "title": "Correct Pool Car Mileage",
        "description": "Corrected readings replace the effective values. The original values and reason remain in the audit history.",
        "submitButtonLabel": "Save correction",
        "type": "ignoreAutoAction",
        "location": `${BOOKINGS_LOCATION}/${booking.ID}/Mileage`,
        "fields": [
            ...(hasPickup ? [{ "label": "Pickup mileage", "name": "PickupMileage", "type": "number" }] : []),
            ...(hasReturn ? [{ "label": "Return mileage", "name": "ReturnMileage", "type": "number" }] : []),
            { "label": "Correction reason", "name": "Reason", "type": "text", "divClass": "md:col-span-3" },
        ],
        preSubmitHook({ data }) {
            const pickupMileage = hasPickup ? validateMileage(data.PickupMileage) : null;
            const returnMileage = hasReturn ? validateMileage(data.ReturnMileage) : null;
            if (hasReturn && returnMileage < pickupMileage) {
                throw new Error("Return mileage cannot be lower than pickup mileage.");
            }
            if (!String(data.Reason || "").trim()) {
                throw new Error("Enter a reason for the correction.");
            }
            return { "PickupMileage": pickupMileage, "ReturnMileage": returnMileage };
        },
        async postSubmitHook({ data }) {
            const correctionRef = db.ref(`${BOOKINGS_LOCATION}/${booking.ID}/Mileage/Corrections`).push();
            const result = await db.ref(`${BOOKINGS_LOCATION}/${booking.ID}/Mileage`).transaction(current => {
                if (!current) return;
                const timestamp = Date.now();
                const corrected = {
                    ...current,
                    "ReviewRequired": false,
                    "Corrections": {
                        ...(current.Corrections || {}),
                        [correctionRef.key]: {
                            "OldPickupMileage": hasPickup ? current.Start.Value : null,
                            "NewPickupMileage": data.PickupMileage,
                            "OldReturnMileage": hasReturn ? current.End.Value : null,
                            "NewReturnMileage": data.ReturnMileage,
                            "Reason": String(data.Reason).trim(),
                            "Corrected": new Date(timestamp).toISOString(),
                            "CorrectedTimestamp": timestamp,
                            "UserID": store.getters.userID,
                            "AuthUID": auth.currentUser.uid,
                        },
                    },
                };
                if (hasPickup) corrected.Start = { ...current.Start, "Value": data.PickupMileage };
                if (hasReturn) {
                    corrected.End = { ...current.End, "Value": data.ReturnMileage };
                    corrected.Distance = data.ReturnMileage - data.PickupMileage;
                }
                return corrected;
            });
            if (!result.committed) throw new Error("The mileage correction could not be saved.");
        },
        "successMessage": "Mileage corrected",
    });
    form.toggle();
};

export const addBooking = ({ form, store, db, slot }) => {
    const startHour = slot.hour;
    const endHour = slot.endHour ?? Math.min(startHour + 1, slot.lastHour);
    const startOptions = slot.allDay
        ? [{ "id": "0", "label": "00:00" }]
        : hourOptions(slot.firstHour, slot.lastHour, true, false);
    const endOptions = slot.allDay
        ? [{ "id": "24", "label": "24:00" }]
        : hourOptions(slot.firstHour, slot.lastHour, true, true);

    form.setValues({
        "Date": slot.date,
        "EndDate": slot.endDate ?? slot.date,
        "StartHour": String(startHour),
        "EndHour": String(endHour),
        "Purpose": "",
        "AssignedUserID": store.getters.userID,
    });
    form.setConfig({
        "title": slot.allDay ? "Book Pool Car for Full Day(s)" : "Book Pool Car",
        "description": slot.allDay ? "Select the final day required. The booking includes the whole end date." : "",
        "type": "push",
        "location": BOOKINGS_LOCATION,
        "fields": [{
            "label": "Start date",
            "name": "Date",
            "type": "date",
        }, {
            "label": slot.allDay ? "End date (inclusive)" : "End date",
            "name": "EndDate",
            "type": "date",
        }, {
            "label": "Start",
            "name": "StartHour",
            "type": "dropdown",
            "dropdownData": startOptions,
        }, {
            "label": "End",
            "name": "EndHour",
            "type": "dropdown",
            "dropdownData": endOptions,
        }, {
            "label": "User",
            "name": "AssignedUserID",
            "type": "dropdown",
            "dataLocation": "users",
            "labelKey": "name",
            "idKey": "key",
            dataFilter(user) {
                return user.main === true || user.main === "true";
            },
        }, {
            "label": "Commercial",
            "name": "CommercialID",
            "type": "dropdown",
            "dataLocation": "Database/Commercials",
            "labelKey": "Number",
            "idKey": "ID",
        },{
            "label": "Purpose",
            "name": "Purpose",
            "type": "text",
            "required": false,
            "divClass": "md:col-span-3",
        }],
        async preSubmitHook({ data,  }) {
            const start = timestampFor(data.Date, data.StartHour);
            const end = timestampFor(data.EndDate, data.EndHour);

            if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
                throw new Error("The booking end date and time must be later than its start date and time.");
            }

            const currentBookings = await db.once(BOOKINGS_LOCATION);
            const conflict = conflictingBooking(currentBookings, start, end);
            if (conflict) {
                throw new Error(
                    `The pool car is already booked by ${conflict.UserName || "another user"} from ${formatBookingTime(conflict)}.`
                );
            }

            const selectedCommercial = data.CommercialID ? await db.once(`Database/Commercials/${data.CommercialID}`) : null;
            if (data.CommercialID && !selectedCommercial) {
                throw new Error("The selected commercial is not valid.");
            }

            if (!data.AssignedUserID) {
                throw new Error("A user must be selected for this booking.");
            }

            const selectedUser = await db.once(`users/${data.AssignedUserID}`);
            if (!selectedUser) {
                throw new Error("The selected user could not be found.");
            }
            if (selectedUser.main !== true) {
                throw new Error("The selected user is not a main user.");
            }

            return {
                "Start": start,
                "End": end,
                "UserID": data.AssignedUserID,
                "UserName": selectedUser.name,
                "AuthUID": data.AssignedUserID,
                "BookedByAuthUID": auth.currentUser.uid,
                "BookedByUserID": store.getters.userID,
                "BookedByName": store.getters.userName,
                "Created": new Date().toISOString(),
                "Cancelled": false,
                "Commercial": selectedCommercial?.Number || null,
                "Mileage": initialMileage(),
            };
        },
        async postSubmitHook({ data }) {
            if (data.CommercialID) {
                await db.set(`Database/Commercials/${data.CommercialID}/PoolCarBooking/${data.ID}`, true);
            }
        },
        "successMessage": "Pool car booked",
    });
    form.getDropdownData();
    form.toggle();
};

export const cancelBooking = ({ form, booking, toast, db, router }) => {
    const currentAuthUID = auth.currentUser?.uid;
    const isBooker = Boolean(currentAuthUID) && booking?.BookedByAuthUID === currentAuthUID;
    if (!isAuthOwner(booking) && !isBooker) {
        toast.error("Only the assigned user or the person who made this booking can cancel it.");
        return;
    }
    if (!canManageBooking(booking, currentAuthUID)) {
        toast.error("Bookings can only be cancelled before they start and before pickup mileage is recorded.");
        return;
    }

    form.setConfig({
        "title": "Cancel Pool Car Booking",
        "description": `Cancel your pool car booking from <strong>${formatBookingTime(booking)}</strong>?`,
        "submitButtonLabel": "Cancel booking",
        "submitButtonClass": "cancel",
        "type": "delete",
        "location": `${BOOKINGS_LOCATION}/${booking.ID}`,
        "fields": [],
        async postSubmitHook() {
            if (db && booking.CommercialID) {
                await db.delete(`Database/Commercials/${booking.CommercialID}/PoolCarBooking/${booking.ID}`);
            }
            if (router) await router.replace("/PoolCarBooking");
        },
        "successMessage": "Pool car booking cancelled",
    });
    form.toggle();
};

export const cancelBookingFromPage = ({ pageItem: booking, ...context }) => cancelBooking({ ...context, booking });

export const cancelBookingAction = {
    label: "Cancel Booking",
    class: "text-red-600",
    action: cancelBookingFromPage,
    show({ pageItem }) {
        return [canEditBooking(pageItem), "Only the assigned user or booker can cancel before the booking starts and pickup is recorded."];
    },
};
